/**
 * VA-27781 — the access/refresh tokens live in cookies, expiring with the token.
 *
 * They used to live in `localStorage`, which never expires: the token sat there
 * long after it was dead, `PrivateRoute` saw a non-empty string and let the user
 * in, and every request behind the page failed 401 with no explanation. Storing
 * them in a cookie whose `Max-Age` is derived from the JWT's own `exp` means the
 * browser removes them at exactly the moment they stop working, so "no token"
 * and "dead token" become the same state — and that state already redirects to
 * login.
 *
 * Persistent, not session, cookies: closing the browser must NOT lose a session
 * that still has hours left on it.
 *
 * Written to be a no-op for everything already working:
 *
 *  - **Reads fall back to localStorage.** Anyone already signed in keeps their
 *    session across the deploy, and any read site not yet converted still works.
 *  - **Migration is automatic and one-way.** On first load a localStorage token is
 *    copied into a cookie and removed, so nobody is logged out by the change.
 *  - **A blocked cookie falls back silently.** In the ATS embed we run as a
 *    cross-origin iframe, where third-party cookies may be refused outright
 *    (Safari blocks them; Chrome is phasing them out). Every write is verified by
 *    reading it back, and on failure we keep using localStorage rather than
 *    losing the session. The embed keeps working exactly as before.
 *
 * NOTE ON SECURITY, so nobody mistakes what this buys: a cookie written from
 * JavaScript cannot be `HttpOnly`, so it is exactly as readable by injected
 * script as localStorage was. This change is about lifetime and correctness, not
 * XSS. Real XSS protection needs the server to set `HttpOnly; Secure; SameSite`
 * and the client to stop attaching `Authorization` — an IAM-side project, since
 * `access` is minted by the shared service.
 */
import { getJwtExpiry, unquote } from './auth-session';
export const ACCESS_KEY = 'access';
export const REFRESH_KEY = 'refresh';
/**
 * Lifetime for a token whose `exp` we cannot read. Long enough to behave like the
 * old localStorage session, short enough that a stale one cannot linger forever.
 */
const FALLBACK_TTL_SEC = 12 * 60 * 60;
/** Don't bother storing a token with less than this left — it is already dead. */
const MIN_TTL_SEC = 5;
const isBrowser = () => typeof document !== 'undefined' && typeof window !== 'undefined';
const domainAttribute = () => '';
/** `Secure` only on https — otherwise the cookie is silently dropped on http dev. */
const secureAttribute = () => window.location.protocol === 'https:' ? '; Secure' : '';
export const readCookie = (name) => {
    if (!isBrowser())
        return null;
    const hit = document.cookie
        .split('; ')
        .find((entry) => entry.split('=')[0] === name);
    return hit ? decodeURIComponent(hit.split('=').slice(1).join('=') || '') : null;
};
const legacySharedDomain = () => {
    const host = window.location.hostname || '';
    if (/^(localhost|127\.0\.0\.1|\[?::1\]?)$/.test(host))
        return '';
    if (/^\d+\.\d+\.\d+\.\d+$/.test(host))
        return '';
    const match = host.match(/([^.]+\.[^.]+)$/);
    return match ? `; Domain=.${match[1]}` : '';
};
const writeCookie = (name, value, maxAgeSec) => {
    const legacy = legacySharedDomain();
    if (legacy) {
        document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${secureAttribute()}${legacy}`;
    }
    document.cookie =
        `${name}=${encodeURIComponent(value)}; Max-Age=${Math.floor(maxAgeSec)}` +
            `; Path=/; SameSite=Lax${secureAttribute()}${domainAttribute()}`;
};
const deleteCookie = (name) => {
    if (!isBrowser())
        return;
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${secureAttribute()}`;
    const legacy = legacySharedDomain();
    if (legacy) {
        document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${secureAttribute()}${legacy}`;
    }
};
/**
 * Seconds this token should be kept for: exactly as long as it is valid.
 * Returns 0 for a token that is already expired — there is nothing to store.
 */
export const ttlForToken = (token, nowMs = Date.now()) => {
    const exp = getJwtExpiry(token);
    if (exp === null)
        return FALLBACK_TTL_SEC;
    const remaining = Math.floor((exp * 1000 - nowMs) / 1000);
    return remaining > MIN_TTL_SEC ? remaining : 0;
};
/**
 * Store a token in a cookie that dies with it. Falls back to localStorage when
 * the cookie will not stick, so a blocked-cookie context never loses the session.
 */
const store = (key, rawToken) => {
    const token = unquote(rawToken);
    if (!isBrowser())
        return;
    if (!token) {
        remove(key);
        return;
    }
    const ttl = ttlForToken(token);
    if (ttl <= 0) {
        // Already expired: storing it would recreate the exact bug this fixes.
        remove(key);
        return;
    }
    try {
        writeCookie(key, token, ttl);
    }
    catch (_a) {
        // fall through to the localStorage fallback below
    }
    if (readCookie(key) === token) {
        // The cookie is authoritative now — drop any legacy copy so there is one
        // source of truth and no stale value to outlive it.
        try {
            localStorage.removeItem(key);
        }
        catch (_b) {
            /* storage unavailable */
        }
        return;
    }
    // Cookie refused (third-party iframe, cookies disabled). Keep the session
    // rather than the storage preference.
    try {
        localStorage.setItem(key, JSON.stringify(token));
    }
    catch (_c) {
        /* nothing left to try */
    }
};
const remove = (key) => {
    deleteCookie(key);
    try {
        localStorage.removeItem(key);
    }
    catch (_a) {
        /* storage unavailable */
    }
};
/** Cookie first, then the legacy localStorage copy. */
const read = (key) => {
    const fromCookie = readCookie(key);
    if (fromCookie)
        return fromCookie;
    try {
        return unquote(localStorage.getItem(key)) || null;
    }
    catch (_a) {
        return null;
    }
};
export const writeAccessToken = (token) => store(ACCESS_KEY, token);
export const writeRefreshToken = (token) => store(REFRESH_KEY, token);
export const readAccessToken = () => read(ACCESS_KEY);
export const readRefreshToken = () => read(REFRESH_KEY);
export const USER_DETAILS_KEY = 'user-details';
const USER_DETAILS_MAX_BYTES = 3500;
const byteLength = (value) => {
    try {
        return new TextEncoder().encode(value).length;
    }
    catch (_a) {
        return value.length;
    }
};
export const writeUserDetails = (value) => {
    if (!isBrowser())
        return;
    if (!value) {
        removeUserDetails();
        return;
    }
    try {
        localStorage.setItem(USER_DETAILS_KEY, value);
    }
    catch (_a) {
        /* storage unavailable (private mode) */
    }
    const access = readCookie(ACCESS_KEY) || read(ACCESS_KEY);
    const ttl = access ? ttlForToken(access) : FALLBACK_TTL_SEC;
    if (ttl <= 0)
        return;
    const encoded = encodeURIComponent(value);
    if (byteLength(encoded) > USER_DETAILS_MAX_BYTES) {
        document.cookie = `${USER_DETAILS_KEY}=; Max-Age=0; Path=/; SameSite=Lax${secureAttribute()}`;
        return;
    }
    try {
        writeCookie(USER_DETAILS_KEY, value, ttl);
    }
    catch (_b) {
        /* localStorage copy above is still good */
    }
};
/** Read `user-details`: cookie first, then localStorage. */
export const readUserDetails = () => read(USER_DETAILS_KEY);
export const removeUserDetails = () => remove(USER_DETAILS_KEY);
export const clearTokens = () => {
    remove(ACCESS_KEY);
    remove(REFRESH_KEY);
    // The profile describes the session; it must not outlive it.
    remove(USER_DETAILS_KEY);
};
/**
 * Drop the access token but KEEP the refresh token, so a dead access token can
 * still be renewed silently. See `clearAccessTokenOnly` in auth-session.
 */
export const clearAccessToken = () => remove(ACCESS_KEY);
/**
 * Move any legacy localStorage tokens into cookies, once, on app start.
 *
 * Without this, everyone signed in at deploy time would be writing to cookies
 * only on their NEXT login while still reading a localStorage token that never
 * expires — i.e. the old bug would survive for existing sessions. An expired
 * legacy token is dropped rather than migrated.
 */
export const migrateLegacyTokens = () => {
    if (!isBrowser())
        return;
    [ACCESS_KEY, REFRESH_KEY].forEach((key) => {
        if (readCookie(key))
            return; // already migrated
        let legacy = null;
        try {
            legacy = unquote(localStorage.getItem(key));
        }
        catch (_a) {
            return;
        }
        if (!legacy)
            return;
        store(key, legacy); // stores if still valid, drops it if not
    });
};
/**
 * Run the migration the moment this module is imported, not from an effect.
 *
 * This is the fix for the lockout that followed the localStorage->cookie
 * deploy. `migrateLegacyTokens()` was called from an `App.tsx` useEffect, but
 * `PrivateRoute` reads the token while it renders and `getHeaders()` reads it
 * on the first request -- both of which can run BEFORE that effect fires. For
 * a user who was already signed in at deploy time the cookie did not exist
 * yet, so the guard saw no live session and bounced a perfectly valid session
 * to the login screen (and, on a deep link, to a 404).
 *
 * Importing this module is the only way to read a token at all, so migrating
 * here means no reader can ever observe the pre-migration state. Wrapped
 * because a module-load throw would take the whole bundle down.
 */
try {
    migrateLegacyTokens();
}
catch (_a) {
    /* never let migration break module load */
}
